/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useCallback, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '../../components/ui/modal.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type Session } from '../../lib/api/sessions.js';
import { useClients } from '../../lib/queries/clinical.js';
import { useCancelSession, useSessions, useTransitionSession } from '../../lib/queries/sessions.js';
import { RescheduleForm } from '../admin/reschedule-form.js';

import { RateSessionForm } from './rate-session-form.js';
import { SessionTable } from './session-table.js';

/** Therapist → Sessions (Figma "My Sessions"). Lifecycle + Figma columns + details. */
export function SessionsPage(): JSX.Element {
  const { t } = useTranslation();
  const sessions = useSessions();
  const clients = useClients();
  const transition = useTransitionSession();
  const cancel = useCancelSession();
  const [rescheduling, setRescheduling] = useState<Session | null>(null);
  const [rating, setRating] = useState<Session | null>(null);

  const clientNames = useMemo(
    () => new Map((clients.data ?? []).map((c) => [c.id, c.fullName])),
    [clients.data],
  );

  const onTransition = useCallback(
    (id: string, action: 'start' | 'complete' | 'no-show') => {
      transition.mutate({ id, action });
    },
    [transition],
  );
  const onCancel = useCallback(
    (id: string) => {
      cancel.mutate({ id });
    },
    [cancel],
  );
  const onReschedule = useCallback((session: Session) => {
    setRescheduling(session);
  }, []);
  const onRescheduleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setRescheduling(null);
    }
  }, []);
  const onRate = useCallback((session: Session) => {
    setRating(session);
  }, []);
  const onRatingOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setRating(null);
    }
  }, []);
  const closeReschedule = useCallback(() => {
    setRescheduling(null);
  }, []);
  const closeRating = useCallback(() => {
    setRating(null);
  }, []);

  return (
    <div className="p-8">
      <PageHeader
        title={t('therapist.sessions.title')}
        subtitle={t('therapist.sessions.subtitle')}
      />
      <QueryState
        isLoading={sessions.isLoading}
        isError={sessions.isError}
        isEmpty={(sessions.data ?? []).length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('therapist.sessions.empty')}
      >
        <SessionTable
          sessions={sessions.data ?? []}
          clientNames={clientNames}
          onTransition={onTransition}
          onCancel={onCancel}
          onReschedule={onReschedule}
          onRate={onRate}
        />
      </QueryState>
      <Modal
        open={rescheduling !== null}
        onOpenChange={onRescheduleOpenChange}
        title={t('therapist.sessions.rescheduleTitle')}
        description={t('therapist.sessions.rescheduleSubtitle')}
        closeLabel={t('common.cancel')}
      >
        {rescheduling !== null && (
          <RescheduleForm
            sessionId={rescheduling.id}
            currentStart={rescheduling.scheduledStart}
            onDone={closeReschedule}
          />
        )}
      </Modal>
      <Modal
        open={rating !== null}
        onOpenChange={onRatingOpenChange}
        title={t('therapist.rate.title')}
        description={t('therapist.rate.subtitle')}
        closeLabel={t('common.cancel')}
      >
        {rating !== null && <RateSessionForm session={rating} onDone={closeRating} />}
      </Modal>
    </div>
  );
}
